import { Link, useLocation } from "react-router-dom";
import { HOME, LOGIN, SIGNUP, ABOUT_ME } from "routes/CONSTANTS";
import { Button } from "components/widgets/button";
import { useAppSelector, useAppDispatch } from "hooks";
import { logout } from "../../../redux/slices/auth.slice";
import { motion, useCycle, AnimatePresence } from "framer-motion";
import { Drawer } from "../drawer";
import { Menu } from "components/widgets";
import { TesLogout } from "components/icons";
import { user } from "assets/images";
import { ogLogo } from "assets/logos";
import NavLink from "./NavLink";
import { toast } from "react-toastify";
import React, { useState, useEffect } from "react";


interface NavbarProps {
  isSidebarOpen?: boolean;
  hasSidebar?: boolean;
}

const rollAnimation = {
  rotate: [0, 360],
  transition: {
    repeat: Infinity,
    repeatType: "loop" as const,
    duration: 2,
    ease: "linear"
  }
};

// Define navbar themes
const navbarThemes = [
  {
    name: "Neon Glow",
    className:
      "bg-black/50 backdrop-blur-lg border-b-2 border-cyan-400/70 shadow-[0_0_20px_rgba(34,211,238,0.5)]"
  },
  {
    name: "My Theme Enhanced",
    className:
      "bg-gradient-to-r from-green-900/30 to-emerald-900/30 backdrop-blur-lg border-b-2 border-yellow-500/50 shadow-[0_4px_24px_rgba(234,179,8,0.2)]"
  },
  {
    name: "Elegant Double Border",
    className:
      "bg-gray-950/50 backdrop-blur-md border-b-2 border-double border-emerald-400/60 shadow-2xl"
  },
  {
    name: "Cyberpunk Style",
    className:
      "bg-black/60 backdrop-blur-xl border-b-2 border-pink-500/70 shadow-[0_0_30px_rgba(236,72,153,0.4)]"
  }
];

const Navbar: React.FC<NavbarProps> = ({ isSidebarOpen = false, hasSidebar = false }) => {
  const dispatch = useAppDispatch();
  const { isLoggedIn } = useAppSelector((state) => state.auth);
  const location = useLocation();
  const [open, toggle] = useCycle(false, true);
  const [currentTheme, setCurrentTheme] = useState(navbarThemes[0]);

  // Change theme randomly on route change
  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * navbarThemes.length);
    setCurrentTheme(navbarThemes[randomIndex]);
  }, [location.pathname]);

  const handleLogout = () => {
    try {
      void dispatch(logout());
    } catch (error: any) {
      toast.error(error);
    }
  };

  return (
    <motion.div
      initial={false}
      animate={{
        left: hasSidebar && isSidebarOpen ? "10rem" : "0",
        width: hasSidebar && isSidebarOpen ? "calc(100% - 15rem)" : "100%"
      }}
      transition={{ duration: 0.3 }}
      className={`fixed top-0 h-16 z-40 transition-all duration-500 ${currentTheme.className}`}
    >
      <div className="flex flex-row items-center justify-between h-full px-3 sm:px-6">
        {/* Logo */}
        <Link to={HOME} className="flex items-center">
          <motion.img 
            src={ogLogo} 
            alt="logo" 
            animate={rollAnimation} 
            className="w-10 h-10 sm:w-12 sm:h-12 ml-10 sm:ml-10" 
          />
        </Link>

        {/* Desktop Navigation */}
        {!isLoggedIn ? (
          <div className="hidden lg:flex items-center gap-4">
            {location.pathname !== "/login" && (
              <NavLink to={LOGIN}>
                <span className="text-yellow-400 hover:text-yellow-300 transition">Log In</span>
              </NavLink>
            )}
            {location.pathname !== "/auth/register" && (
              <Button
                to={SIGNUP}
                variant="full"
                className="bg-yellow-500 text-black font-semibold border border-black hover:bg-black hover:text-yellow-400 rounded-full transition-all px-6 py-2"
              >
                Sign Up
              </Button>
            )}
          </div>
        ) : (
          <div className="hidden lg:flex items-center gap-4">
            <Link to={ABOUT_ME} className="group">
              <img
                src={user}
                alt="Profile"
                className="shadow-lg rounded-full w-10 h-10 border-2 border-yellow-500 group-hover:border-yellow-400 transition-all"
              />
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-red-600/80 hover:bg-red-700 text-white rounded-lg transition-all shadow-md"
              aria-label="Logout"
            >
              <TesLogout size={20} color="white" />
              <span className="text-sm font-medium">Logout</span>
            </button>
          </div>
        )}

        {/* Mobile Menu Button */}
       {/* Mobile Actions */}
<div className="flex items-center lg:hidden gap-3">
  {/* Show Login / Signup on mobile when NOT logged in */}
  {!isLoggedIn && (
    <>
      {location.pathname !== "/login" && (
        <NavLink to={LOGIN}>
          <span className="text-yellow-400 text-sm font-medium">
            Log In
          </span>
        </NavLink>
      )}

      {location.pathname !== "/auth/register" && (
        <Button
          to={SIGNUP}
          variant="full"
          className="bg-yellow-500 text-black text-sm font-semibold border border-black rounded-full px-4 py-1"
        >
          Sign Up
        </Button>
      )}
    </>
  )}

  {/* Mobile Profile Icon (only when logged in) */}
  {isLoggedIn && (
    <Link to={ABOUT_ME} className="group">
      <img
        src={user}
        alt="Profile"
        className="shadow-lg rounded-full w-8 h-8 border-2 border-yellow-500"
      />
    </Link>
  )}

  {/* Hamburger Menu */}
  <Menu toggle={toggle} isOpen={open} />
</div>
</div>


      <AnimatePresence>{open && <Drawer open={open} />}</AnimatePresence>
    </motion.div>
  );
};

export default Navbar;